<template>
  <el-upload
    v-bind="attrs"
    :accept="accept"
    :before-upload="beforeUpload"
    class="drop-uploaded-file"
    drag
    :http-request="handleUploadRequest"
  >
    <slot v-if="slots.default"></slot>
    <template v-else>
      <img alt="" class="drop-uploaded-file__icon" :src="dropIcon" />
      <div class="drop-uploaded-file__content">
        <div class="drop-uploaded-file__title">拖拽或点击此处上传本地文件</div>
        <div class="drop-uploaded-file__tip">
          支持 {{ acceptText }} 格式，总文件大小上限为 {{ maxSize }}MB
        </div>
      </div>
    </template>

    <template v-if="slots.tip" #tip>
      <slot name="tip"></slot>
    </template>
    <template v-if="slots.trigger" #trigger>
      <slot name="trigger"></slot>
    </template>
    <template v-if="slots.file" #file="{ file }">
      <slot :file="file" name="file"></slot>
    </template>
  </el-upload>
</template>

<script setup>
import { computed, useAttrs, useSlots } from 'vue'
import AppMessage from '@/components/message/index.js'
import dropIcon from '@/assets/icon/icon-drop.svg'
import { useOSS } from '@/utils/oss'

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  accept: {
    type: String,
    default: '.pdf,.docx,.doc,.md'
  },
  maxSize: {
    type: [Number, String],
    default: 20
  }
})

const attrs = useAttrs()
const slots = useSlots()
const { uploadFile } = useOSS()

const acceptText = computed(() => {
  const types = props.accept
    .split(',')
    .map((item) => item.trim().replace(/^\./, '').replace(/\/\*$/, '').replace(/^.+\//, ''))
    .filter(Boolean)

  return types.length ? types.join('、') : 'pdf、docx、doc、md'
})

const getMaxSizeBytes = () => Number(props.maxSize) * 1024 * 1024
const getFileList = () => attrs.fileList || attrs['file-list'] || []
const getAcceptRules = () =>
  props.accept
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)

const getFileExt = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.')
  return lastDotIndex > -1 ? fileName.slice(lastDotIndex + 1).toLowerCase() : ''
}

const isAcceptedFile = (file) => {
  const rules = getAcceptRules()
  if (!rules.length) {
    return true
  }

  const fileExt = getFileExt(file.name)
  const fileType = (file.type || '').toLowerCase()

  return rules.some((rule) => {
    if (rule.startsWith('.')) {
      return fileExt === rule.slice(1)
    }
    if (rule.endsWith('/*')) {
      return fileType.startsWith(rule.slice(0, -1))
    }
    if (rule.includes('/')) {
      return fileType === rule
    }
    return fileExt === rule
  })
}

const updateFileList = (fileList) => {
  const updateHandler = attrs['onUpdate:fileList'] || attrs['onUpdate:file-list']
  if (Array.isArray(updateHandler)) {
    updateHandler.forEach((handler) => handler(fileList))
    return
  }
  updateHandler?.(fileList)
}

const getCurrentTotalSize = (excludeUid) => {
  return getFileList().reduce((total, file) => {
    if (file.uid === excludeUid) {
      return total
    }
    return total + (file.raw?.size || file.size || 0)
  }, 0)
}

const beforeUpload = (rawFile) => {
  if (!isAcceptedFile(rawFile)) {
    AppMessage.error(`仅支持 ${acceptText.value}格式文件`)
    return false
  }

  if (getCurrentTotalSize(rawFile.uid) + rawFile.size > getMaxSizeBytes()) {
    AppMessage.error(`文件大小总和不能超过 ${props.maxSize}MB，请重新选择`)
    return false
  }

  return true
}

const updateUploadSuccessFile = (rawFile, response) => {
  updateFileList(
    getFileList().map((file) => {
      if (file.uid !== rawFile.uid) {
        return file
      }

      return {
        uid: file.uid,
        ...response
      }
    })
  )
}

const getUploadResponse = (file, attachIndex) => ({
  attachIndex,
  filename: file.name,
  size: file.size,
  ext: getFileExt(file.name)
})

const handleUploadRequest = ({ file, onError, onProgress, onSuccess }) => {
  uploadFile(
    file,
    (percent) => {
      onProgress?.({ percent })
    },
    false,
    false,
    true
  )
    .then((url) => {
      const response = getUploadResponse(file, url)
      onSuccess?.(response)
      updateUploadSuccessFile(file, response)
    })
    .catch((error) => {
      onError?.(error)
      AppMessage.error(error?.message || '文件上传失败，请稍后重试')
    })
}
</script>

<style lang="scss" scoped>
$drop-upload-bg: linear-gradient(280deg, #f7f5ff 1%, #f3f7ff 100%); // paint_6708:142437
$drop-upload-border: #e5e6eb; // 线条/Line-2 一般线条
$drop-upload-title: #323233; // 文字/Text-4 强调正文标题
$drop-upload-tip: #969799; // 文字/Text-2 次要信息
$drop-upload-hover: #7a52f4;

.drop-uploaded-file {
  width: 100%;
  font-family: 'PingFang SC', sans-serif;

  :deep(.el-upload) {
    display: block;
    width: 100%;
  }

  :deep(.el-upload-dragger) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 16px;
    box-sizing: border-box;
    width: 100%;
    height: 134px;
    padding: 24px 12px;
    border: 1px dashed $drop-upload-border;
    border-radius: 16px;
    background: $drop-upload-bg;
  }

  :deep(.el-upload-dragger:hover) {
    border-color: $drop-upload-hover;
  }
}

.drop-uploaded-file__icon {
  display: block;
  width: 24px;
  height: 24px;
}

.drop-uploaded-file__content {
  width: 100%;
  text-align: center;
}

.drop-uploaded-file__title {
  color: $drop-upload-title;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.drop-uploaded-file__tip {
  margin-top: 4px;
  color: $drop-upload-tip;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
